"use client";

import React from "react";
import Image from "next/image";
import styles from "./ResponsiveImage.module.css";

export interface ImageSource {
  src: string;
  width: number;
  height: number;
}

interface ResponsiveImageProps {
  desktop?: ImageSource | null;
  tablet?: ImageSource | null;
  mobile?: ImageSource | null;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  /** Next image quality. Defaults to 75; raise only for large renders. */
  quality?: number;
}

/**
 * Renders a CMS-sourced image at the correct intrinsic aspect ratio.
 * Next.js <Image> uses `width` / `height` to compute the aspect ratio
 * applied when CSS sets `height: auto`, so we must forward the *real*
 * Cloudinary dimensions, not a hardcoded 3:2 fallback (the previous
 * implementation forced every image into 1200×800).
 */
const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  desktop,
  tablet,
  mobile,
  alt,
  className,
  sizes,
  priority,
  quality,
}) => {
  const mobileImage = mobile || tablet || desktop;
  const desktopImage = desktop || tablet || mobile;

  if (!mobileImage && !desktopImage) return null;

  // Same image at every breakpoint → render once.
  if (
    !mobileImage ||
    !desktopImage ||
    mobileImage.src === desktopImage.src
  ) {
    const img = mobileImage || desktopImage;
    if (!img) return null;
    return (
      <Image
        src={img.src}
        alt={alt}
        width={img.width}
        height={img.height}
        className={className}
        sizes={sizes}
        priority={priority}
        quality={quality}
      />
    );
  }

  // Different images per breakpoint: render both with visibility toggling.
  return (
    <>
      <Image
        src={mobileImage.src}
        alt={alt}
        width={mobileImage.width}
        height={mobileImage.height}
        className={`${className || ""} ${styles.mobileOnly}`.trim()}
        sizes={sizes}
        priority={priority}
        quality={quality}
      />
      <Image
        src={desktopImage.src}
        alt={alt}
        width={desktopImage.width}
        height={desktopImage.height}
        className={`${className || ""} ${styles.desktopOnly}`.trim()}
        sizes={sizes}
        priority={priority}
        quality={quality}
      />
    </>
  );
};

export default ResponsiveImage;
